<!-- src/app/pages/dashboard/t0-dashboard/t0-dashboard.component.html -->
@if (loadingBaseline) {
<div class="bg-surface-50 flex flex-col gap-6">
    <div class="flex flex-col xl:flex-row gap-4">
        <div class="flex-1 flex flex-col gap-4">
            <!-- Stats cards (matches t0-stats: xl:grid-cols-5) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                @for (i of [0,1,2,3,4]; track i) {
                <div class="bg-surface-0 rounded-2xl shadow-lg px-5 py-4 flex flex-col gap-2">
                    <p-skeleton width="6rem" height="10px" borderRadius="9999px"></p-skeleton>
                    <p-skeleton width="4.5rem" height="20px" borderRadius="6px"></p-skeleton>
                    <div class="flex items-center gap-2 mt-1">
                        <p-skeleton width="100%" height="6px" borderRadius="9999px"></p-skeleton>
                        <p-skeleton width="2rem" height="11px" borderRadius="9999px"></p-skeleton>
                    </div>
                </div>
                }
            </div>

            <!-- Comparison bar (matches rose banner + 2 selects) -->
            <div class="bg-surface-0 rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg">
                <div class="flex-1 flex flex-col gap-2">
                    <p-skeleton width="70%" height="12px" borderRadius="9999px"></p-skeleton>
                    <p-skeleton width="45%" height="12px" borderRadius="9999px"></p-skeleton>
                </div>
                <div class="flex items-center gap-2">
                    <p-skeleton width="9rem" height="32px" borderRadius="9999px"></p-skeleton>
                    <p-skeleton width="8rem" height="32px" borderRadius="9999px"></p-skeleton>
                </div>
            </div>

            <!-- Projected impact (matches lg:grid-cols-2) -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Cascade of Care -->
                <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4 lg:px-6 lg:py-5">
                    <div class="flex flex-col gap-1 mb-3">
                        <p-skeleton width="8rem" height="14px" borderRadius="9999px"></p-skeleton>
                        <p-skeleton width="12rem" height="10px" borderRadius="9999px"></p-skeleton>
                    </div>
                    <p-skeleton width="100%" height="280px" borderRadius="12px"></p-skeleton>
                </div>

                <!-- Projected impact list -->
                <div class="bg-surface-0 rounded-2xl shadow-lg overflow-hidden h-full">
                    <div class="px-4 py-4 lg:px-6 lg:py-5 border-b border-surface-200 flex items-center justify-between">
                        <div class="flex flex-col gap-2">
                            <p-skeleton width="9rem" height="14px" borderRadius="9999px"></p-skeleton>
                            <p-skeleton width="16rem" height="10px" borderRadius="9999px"></p-skeleton>
                        </div>
                        <p-skeleton width="5rem" height="30px" borderRadius="6px"></p-skeleton>
                    </div>

                    <div class="divide-y divide-surface-200">
                        @for (row of [0,1,2,3]; track row) {
                        <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                            <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
                            <div class="flex-1 flex flex-col gap-2">
                                <p-skeleton width="6rem" height="13px" borderRadius="9999px"></p-skeleton>
                                <p-skeleton width="10rem" height="10px" borderRadius="9999px"></p-skeleton>
                            </div>
                            <div class="flex flex-col items-end gap-2">
                                <p-skeleton width="3rem" height="12px" borderRadius="9999px"></p-skeleton>
                                <p-skeleton width="2.5rem" height="10px" borderRadius="9999px"></p-skeleton>
                            </div>
                        </div>
                        }
                    </div>

                    <div class="px-4 py-3 lg:px-6 bg-surface-50">
                        <p-skeleton width="80%" height="10px" borderRadius="9999px"></p-skeleton>
                    </div>
                </div>
            </div>

            <!-- Scenarios (matches single line chart, 420px) -->
            <div class="grid grid-cols-1 gap-4">
                <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4 lg:px-6 lg:py-5">
                    <div class="flex flex-col md:flex-row justify-between gap-3 mb-3">
                        <p-skeleton width="10rem" height="14px" borderRadius="9999px"></p-skeleton>
                        <div class="flex items-center gap-2">
                            <p-skeleton width="10rem" height="32px" borderRadius="6px"></p-skeleton>
                            <p-skeleton width="9rem" height="32px" borderRadius="6px"></p-skeleton>
                        </div>
                    </div>
                    <p-skeleton width="100%" height="420px" borderRadius="12px"></p-skeleton>
                </div>
            </div>
        </div>

        <!-- Simulation panel (matches xl:w-80 sidebar) -->
        <div class="w-full xl:w-80 flex flex-col gap-4">
            <!-- Coverage doughnut -->
            <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4">
                <p-skeleton width="11rem" height="11px" borderRadius="9999px" styleClass="mb-4"></p-skeleton>
                <div class="flex items-center gap-4">
                    <p-skeleton shape="circle" size="6rem"></p-skeleton>
                    <div class="flex flex-col gap-3 flex-1">
                        @for (l of [0,1,2,3]; track l) {
                        <div class="flex items-center gap-2">
                            <p-skeleton shape="circle" size="0.5rem"></p-skeleton>
                            <p-skeleton width="7rem" height="11px" borderRadius="9999px"></p-skeleton>
                        </div>
                        }
                    </div>
                </div>
            </div>

            <!-- Simulation parameters -->
            <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4">
                <p-skeleton width="10rem" height="14px" borderRadius="9999px" styleClass="mb-4"></p-skeleton>

                <div class="flex flex-col gap-4">
                    @for (k of [0,1,2]; track k) {
                    <div class="flex items-center gap-3">
                        <p-skeleton shape="circle" size="3.75rem"></p-skeleton>
                        <div class="flex-1 flex flex-col gap-2">
                            <p-skeleton width="6rem" height="13px" borderRadius="9999px"></p-skeleton>
                            <p-skeleton width="10rem" height="10px" borderRadius="9999px"></p-skeleton>
                            <p-skeleton width="7rem" height="10px" borderRadius="9999px"></p-skeleton>
                        </div>
                    </div>
                    }

                    <p-skeleton width="100%" height="42px" borderRadius="9999px" styleClass="mt-2"></p-skeleton>

                    <div class="border-t border-surface-200 pt-4 flex flex-col gap-3">
                        @for (r of [0,1,2]; track r) {
                        <p-skeleton width="100%" height="14px" borderRadius="9999px"></p-skeleton>
                        }
                        <p-skeleton width="8rem" height="34px" borderRadius="6px" styleClass="mt-2"></p-skeleton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
} @else {
<div class="bg-surface-50 flex flex-col gap-6">
    <div class="flex flex-col xl:flex-row gap-4">
        <div class="flex-1 flex flex-col gap-4">
            <app-t0-stats
                [tooltipTotalOverride]="'Total population is 6,824,245. Of this, 62.3% have hypertension.'"
                [horizon]="horizon">
            </app-t0-stats>

            <div class="bg-rose-50 border border-rose-100 rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-lg">
                <div class="text-xs sm:text-sm text-slate-700">
                    Comparison of base scenario with
                    <span class="font-semibold text-rose-600">{{ selectedInfoSimulation }}</span>
                    for a period of
                    <span class="font-semibold text-rose-600">{{ selectedInfoPeriod }}</span>
                </div>

                <div class="flex items-center gap-2">
                    <select
                        class="bg-white rounded-full px-3 py-1.5 text-xs font-medium text-slate-800 border border-rose-200 focus:outline-none"
                        [(ngModel)]="selectedInfoSimulation"
                        (ngModelChange)="onSimulationChange($event)">
                        @for (r of runs; track r.id) {
                            <option [ngValue]="r.title">{{ r.title }}</option>
                        }
                    </select>

                    <select
                        class="bg-white rounded-full px-3 py-1.5 text-xs font-medium text-slate-800 border border-rose-200 focus:outline-none"
                        [(ngModel)]="selectedInfoPeriod"
                        (ngModelChange)="onPeriodChange($event)">
                        <option [ngValue]="'5 years'">5 years</option>
                        <option [ngValue]="'10 years'">10 years</option>
                        <option [ngValue]="'15 years'">15 years</option>
                        <option [ngValue]="'20 years'">20 years</option>
                    </select>
                </div>
            </div>

            <app-projected-impact
                [selectedInfoSimulation]="selectedInfoSimulation"
                [selectedInfoPeriod]="selectedInfoPeriod"
                [selectedCascadeSimulation]="selectedCascadeSimulation">
            </app-projected-impact>

            <app-t0-scenerios></app-t0-scenerios>
        </div>

        <app-t0-simulation-panel></app-t0-simulation-panel>
    </div>
</div>
}